import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { sanitarywareService } from "../../../services/enhancedCrudService";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import {
  MAX_PDF_FILE_SIZE,
  MAX_PRODUCT_IMAGE_SIZE,
  MAX_PRODUCT_IMAGE_COUNT,
} from "./../../../shared/constant";
import { Crop } from "./../../../shared/crop";

const SanitarywareList = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [pdfs, setPdfs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [defaultFileName, setDefaultFileName] = useState("");
  const [hideFirm, setHideFirm] = useState(false);
  const [fileName, setFileName] = useState("");
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_records: 0,
    per_page: 10,
  });
  const [showModal, setShowModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [activeTab, setActiveTab] = useState("pdf"); // "pdf" or "image"
  const [imageLabel, setImageLabel] = useState("");
  const [selectedImages, setSelectedImages] = useState([]);
  const [isPrimaryImage, setIsPrimaryImage] = useState(false);
  const [croppedFile, setCroppedFile] = useState(null);
  const [productImages, setProductImages] = useState([]);
  const [pdfDetailData, setPdfDetailData] = useState(null);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const [filters, setFilters] = useState({
    search: "",
    type_id: "",
    attribute_filters: [],
    grade: "",
    glaze: "",
    seller_place: "",
    quantity_range: "",
    logo: "",
    packing: "",
    color: "",
    sort_by: "uploaded_at",
    sort_order: "desc",
  });

  const confirmPublishToggle = (productId, action) => {
    Swal.fire({
      title: "Are you sure?",
      text: `This action will ${action} the tile`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, " + action + " it!",
    }).then((result) => {
      if (result.isConfirmed) {
        handlePublishToggle(productId, action);
      }
    });
  };

  const fetchProducts = async () => {
    try {
      setTableLoading(true);
      const params = {
        page: pagination.current_page,
        limit: pagination.per_page,
        ...filters,
      };
      const response = await sanitarywareService.getList(params);
      const { count, results } = response;
      const products = results?.products || [];
      setProducts(products);
      setPagination((prev) => ({
        ...prev,
        total_records: count,
        total_pages: Math.ceil(count / prev.per_page),
        current_page: params.page,
      }));
    } catch (error) {
      console.error("Error fetching products:", error);
      toast.error("Failed to load products");
    } finally {
      setTableLoading(false);
      setLoading(false);
    }
  };

  const debouncedSearch = useCallback(
    (() => {
      let timeoutId;
      return (searchTerm) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          setFilters((prev) => ({
            ...prev,
            search: searchTerm,
          }));
          setPagination((prev) => ({
            ...prev,
            current_page: 1,
          }));
        }, 500);
      };
    })(),
    []
  );

  const handleFilterChange = (e) => {
    const { name, value } = e.target;

    if (name === "search") {
      debouncedSearch(value);
    } else {
      setFilters((prev) => ({
        ...prev,
        [name]: value,
      }));
      setPagination((prev) => ({
        ...prev,
        current_page: 1,
      }));
    }
  };

  const handleClearSearch = () => {
    setFilters((prev) => ({
      ...prev,
      search: "",
    }));
    setPagination((prev) => ({
      ...prev,
      current_page: 1,
    }));
  };

  const handlePageChange = (page) => {
    setPagination((prev) => ({
      ...prev,
      current_page: page,
    }));
  };

  const handlePublishToggle = async (productId, action) => {
    try {
      const response = await sanitarywareService.updatePublishStatus(
        productId,
        { action: action }
      );
      toast.success(response.message);
      fetchProducts();
    } catch (error) {
      console.error("Error updating publish status:", error);
      if (error.status != 401) {
        const errorMsg =
          error?.response?.data?.error ||
          error?.response?.data?.message || "Failed to update publish status";
        toast.error(errorMsg);
      }
    }
  };

  const handleDelete = async (productId) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This action will permanently delete the product.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      setLoading(true);
      try {
        const response = await sanitarywareService.delete(productId);
        toast.success(response.message);
        await Swal.fire("Deleted!", "The product has been deleted.", "success");
        fetchProducts();
      } catch (error) {
        console.error("Delete error:", error);
        const errorMsg =
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          "Failed to update publish status";
        toast.error(errorMsg);
      } finally {
        setLoading(false);
      }
    }
  };

  // PDF functionality commented out
  // const handlePDFFileChange = (e) => {
  //   const files = e.target.files;
  //   if (files.length > 0) {
  //     const validFiles = [];
  //     for (let i = 0; i < files.length; i++) {
  //       const file = files[i];
  //       if (file.type !== "application/pdf") {
  //         toast.error(`File "${file.name}" is not a valid PDF file`);
  //         continue;
  //       }
  //       const maxSize = MAX_PDF_FILE_SIZE * 1024 * 1024;
  //       if (file.size > maxSize) {
  //         toast.error(
  //           `File "${file.name}" size should be less than ${MAX_PDF_FILE_SIZE} MB`
  //         );
  //         continue;
  //       }

  //       validFiles.push(file);
  //     }

  //     if (validFiles.length > 0) {
  //       setSelectedFile(validFiles[0]);
  //     } else {
  //       setSelectedFile(null);
  //       e.target.value = "";
  //     }
  //   }
  // };

  // PDF functionality commented out
  // const resetPDFFileInput = () => {
  //   if (fileInputRef.current) {
  //     fileInputRef.current.value = null;
  //   }
  //   setFileName("");
  //   setSelectedFile(null);
  // };

  // PDF functionality commented out
  // const savePDFUpload = async () => {
  //   if (!selectedFile) {
  //     toast.error("Please select a PDF file to upload");
  //     return;
  //   }
  //   if (!fileName) {
  //     toast.error("Please enter a file name");
  //     return;
  //   }
  //   try {
  //     setUploading(true);
  //     const user = sessionStorage.getItem("user");
  //     const profile = JSON.parse(user);
  //     const pdfName = defaultFileName + " " + fileName + " " + profile.seller_profile.id + ".pdf";
  //     const formData = new FormData();
  //     formData.append("file", selectedFile);
  //     formData.append("name", pdfName);
  //     formData.append("firm_name_hidden", hideFirm);

  //     let result;
  //     if (selectedProduct && selectedProduct.pdfId) {
  //       result = await sanitarywareService.replacePdf(selectedProduct.product_id, selectedProduct.pdfId, formData);
  //       if (result.status == 'success') {
  //         toast.success("PDF replaced successfully");
  //         // Refresh products data to show updated PDF
  //         await fetchProducts();
  //       } else {
  //         toast.error(result.message);
  //       }
  //       handleCloseModal();
  //       navigate("/products/sanitaryware");
  //     } else {
  //       result = await sanitarywareService.uploadPdf(selectedProduct.product_id, formData);
  //       if (result.status == 'success') {
  //         try {
  //           await sanitarywareService.updatePublishStatus(selectedProduct.product_id, {
  //             action: "publish",
  //           });
  //           toast.success("PDF uploaded and sanitaryware published successfully");
  //         } catch (publishError) {
  //           console.error("Error publishing sanitaryware:", publishError);
  //           toast.success("PDF uploaded successfully, but failed to publish sanitaryware");
  //         }
  //         // Refresh products data to show updated PDF
  //         await fetchProducts();
  //         handleCloseModal();
  //         navigate("/products/sanitaryware");
  //       } else {
  //         toast.error(result.message);
  //       }
  //     }
  //     setUploading(false);
  //     resetPDFFileInput();
  //     fetchProducts();
  //   } catch (error) {
  //     let message = selectedProduct && selectedProduct.pdfId
  //       ? "Failed to replace PDF. Please try again."
  //       : "Failed to upload PDF. Please try again.";
  //     if (error.response && error.response.data) {
  //       message =
  //         error.response.data.name.length > 0
  //           ? error.response.data.name[0]
  //           : message;
  //     }
  //     toast.error(message);
  //     console.error("Error fetching data:", error);
  //     setUploading(false);
  //   } finally {
  //     setUploading(false);
  //   }
  // };

  // PDF functionality commented out
  // const handlePDFReplace = async (pdfId, productId) => {
  //   const result = await Swal.fire({
  //     title: "Replace PDF?",
  //     text: "This action will replace the existing PDF with a new one.",
  //     icon: "question",
  //     showCancelButton: true,
  //     confirmButtonColor: "#3085d6",
  //     cancelButtonColor: "#d33",
  //     confirmButtonText: "Yes, replace it!",
  //   });
  //   if (result.isConfirmed) {
  //     try {
  //       const pdfDetail = await sanitarywareService.getPdfDetail(productId, pdfId);
  //       setPdfDetailData(pdfDetail);
  //       const productData = products.find(product => product.product_id === productId);
  //       if (productData) {
  //         setSelectedProduct({ 
  //           ...productData, 
  //           pdfId, 
  //           productId, 
  //           ...pdfDetail,
  //           existingPdfName: pdfDetail.pdf_name,
  //           existingPdfUrl: pdfDetail.pdf_url,
  //           existingPdfId: pdfDetail.pdf_id,
  //           firmNameHidden: pdfDetail.firm_name_hidden
  //         });
          
  //         const user = sessionStorage.getItem("user");
  //         const profile = JSON.parse(user);
  //         const existingFileName = pdfDetail.pdf_name || "";
  //         const firmName = pdfDetail.firm_name_hidden ? "" : (profile.seller_profile.firm_name || "");
  //         setDefaultFileName(
  //           (productData.type || "") +
  //           " " +
  //           (productData.grade || "") +
  //           " " +
  //           firmName +
  //           " " +
  //           (productData.name || "")
  //         );
          
  //         if (existingFileName) {
  //           const fullName = existingFileName.replace('.pdf', '');
  //           const user = sessionStorage.getItem("user");
  //           const profile = JSON.parse(user);
  //           const sellerId = profile.seller_profile.id;
  //           let customName = fullName.replace(new RegExp(` ${sellerId}$`), '');
            
  //           // Remove product info pattern
  //           const productInfoPattern = `${productData.type} ${productData.grade}`;
  //           if (customName.startsWith(productInfoPattern)) {
  //             customName = customName.substring(productInfoPattern.length).trim();
  //           }
            
  //           // Remove firm name if present
  //           const firmName = profile.seller_profile.firm_name;
  //           if (firmName && customName.includes(firmName)) {
  //             customName = customName.replace(firmName, '').trim();
  //           }
            
  //           // Remove product name if present
  //           if (customName.includes(productData.name)) {
  //             customName = customName.replace(productData.name, '').trim();
  //           }
            
  //           setFileName(customName || 'custom');
  //         }
          
  //         setHideFirm(pdfDetail.firm_name_hidden || false);
  //         setShowModal(true);
  //         fetchProductImages(productId);
  //           }
  //     } catch (error) {
  //       console.error("Error getting PDF details:", error);
  //       toast.error("Failed to get PDF details");
  //     }
  //   }
  // };

  // PDF functionality commented out
  // const handlePDFIconClick = (productData) => {
  //   setSelectedProduct(productData);
  //   const user = sessionStorage.getItem("user");
  //   const profile = JSON.parse(user);
  //   setDefaultFileName(
  //     (productData.type || "") +
  //     " " +
  //     (productData.grade || "") +
  //     " " +
  //     (profile.seller_profile.firm_name || "") +
  //     " " +
  //     (productData.name || "")
  //   );
  //   setShowModal(true);
  //   fetchProductImages(productData.product_id);
  // };

  const handleImageReplace = (productData) => {
    setSelectedProduct(productData);
    setActiveTab("image"); // Set to image tab
    setShowModal(true);
    fetchProductImages(productData.product_id);
    
    // Set default filename for image replacement
    const user = sessionStorage.getItem("user");
    const profile = JSON.parse(user);
    const firmName = hideFirm ? "" : (profile.seller_profile.firm_name || "") + " ";
    setDefaultFileName(
      (productData.type || "") +
      " " +
      (productData.grade || "") +
      " " +
      firmName +
      (productData.name || "")
    );
  };

  // PDF functionality commented out
  // const updateDefaultFileName = () => {
  //   if (selectedProduct) {
  //     const user = sessionStorage.getItem("user");
  //     const profile = JSON.parse(user);
  //     const firmName = hideFirm ? "" : (profile.seller_profile.firm_name || "") + " ";
  //     setDefaultFileName(
  //       (selectedProduct.type || "") +
  //       " " +
  //       (selectedProduct.grade || "") +
  //       " " +
  //       firmName +
  //       (selectedProduct.name || "")
  //     );
  //   }
  // };

  const fetchProductImages = async (productId) => {
    try {
      const response = await sanitarywareService.getImages(productId);
      setProductImages(response || []);
    } catch (error) {
      console.error("Error fetching product images:", error);
      setProductImages([]);
    }
  };

  const handleImageUpload = async () => {
    if (!croppedFile) {
      toast.error("Please select and crop an image to upload");
      return;
    }

    if (productImages.length >= MAX_PRODUCT_IMAGE_COUNT) {
      toast.error(`Maximum ${MAX_PRODUCT_IMAGE_COUNT} images allowed`);
      return;
    }

    try {
      setUploading(true);
      const form = new FormData();
      form.append("image", croppedFile);
      form.append("label", imageLabel || croppedFile.name);
      form.append("is_primary", isPrimaryImage ? "true" : "false");

      await sanitarywareService.uploadImages(selectedProduct.product_id, form);
      toast.success("Image uploaded successfully!");

      // Reset form
      setCroppedFile(null);
      setImageLabel("");
      setIsPrimaryImage(false);
      
      // Refresh product images and products data
      await fetchProductImages(selectedProduct.product_id);
      await fetchProducts();

    } catch (error) {
      let message = "Failed to upload image. Please try again.";
      if (error.response && error.response.data) {
        message =
          error.response.data.non_field_errors?.length > 0
            ? error.response.data.non_field_errors[0]
            : message;
      }
      toast.error(message);
      console.error("Upload failed:", error.response?.data || error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteImage = async (imageId) => {
    if (!imageId) {
      toast.error("Invalid image ID");
      return;
    }

    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This action will permanently delete the image.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        await sanitarywareService.deleteImage(selectedProduct.product_id, imageId);
        toast.success("Image deleted successfully");
        await fetchProductImages(selectedProduct.product_id);
      } catch (error) {
        console.error("Error deleting image:", error);
        if (error.response && error.response.data) {
          const errorData = error.response.data;
            if (errorData.advice) {
            toast.error(errorData.advice);
            return;
          }
              if (errorData.error) {
            toast.error(errorData.error);
            return;
          }
          if (errorData.message) {
            toast.error(errorData.message);
            return;
          }
        }
        toast.error("Failed to delete image. Please try again.");
      }
    }
  };

  const handleSetPrimary = async (imageId) => {
    try {
      await sanitarywareService.changePrimaryImage(selectedProduct.product_id, imageId);
      toast.success("Primary image updated successfully");
      await fetchProductImages(selectedProduct.product_id);
    } catch (error) {
      console.error("Error setting primary image:", error);
      toast.error("Failed to set primary image");
    }
  };


  const handleOpenModal = (product) => {
    setSelectedProduct(product);
    setShowModal(true);
    setActiveTab("image"); // Default to image tab since PDF is commented out
    fetchProductImages(product.product_id);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedProduct(null);
    setHideFirm(false);
    setActiveTab("image"); // Reset to image tab when modal closes since PDF is commented out
    setSelectedFile(null);
    setFileName("");
    setImageLabel("");
    setSelectedImages([]);
    setIsPrimaryImage(false);
    setCroppedFile(null);
    setProductImages([]);
    setPdfDetailData(null); // Reset PDF detail data
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const renderPagination = () => {
    const pages = [];
    const { current_page, total_pages } = pagination;

    for (let i = 1; i <= total_pages; i++) {
      if (
        i === 1 ||
        i === total_pages ||
        (i >= current_page - 2 && i <= current_page + 2)
      ) {
        pages.push(i);
      } else if (pages[pages.length - 1] !== "...") {
        pages.push("...");
      }
    }

    return (
      <nav>
        <ul className="pagination justify-content-center">
          <li className={`page-item ${current_page === 1 ? "disabled" : ""}`}>
            <button
              className="page-link"
              onClick={() => handlePageChange(current_page - 1)}
              disabled={current_page === 1}
            >
              Previous
            </button>
          </li>

          {pages.map((page, index) => (
            <li
              key={index}
              className={`page-item ${page === current_page ? "active" : ""} ${page === "..." ? "disabled" : ""
                }`}
            >
              {page === "..." ? (
                <span className="page-link">...</span>
              ) : (
                <button
                  className="page-link"
                  onClick={() => handlePageChange(page)}
                >
                  {page}
                </button>
              )}
            </li>
          ))}

          <li
            className={`page-item ${current_page === total_pages ? "disabled" : ""
              }`}
          >
            <button
              className="page-link"
              onClick={() => handlePageChange(current_page + 1)}
              disabled={current_page === total_pages}
            >
              Next
            </button>
          </li>
        </ul>
      </nav>
    );
  };

  // PDF functionality commented out
  // useEffect(() => {
  //   updateDefaultFileName();
  // }, [hideFirm]);

  useEffect(() => {
    fetchProducts();
  }, [pagination.current_page, filters]);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(isMobile);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (loading) {
    return (
      <div className="row">
        <div className="col-md-12">
          <div className="card">
            <div className="card-body">
              <div className="text-center">
                <div className="spinner-border" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="row">
      <div className="col-md-12">
        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between">
            <h4 className="card-title">Sanitaryware Products</h4>
            <div className="card-actions">
              <Link
                to="/products/sanitaryware/create"
                className="btn btn-primary"
              >
                Add New Product
              </Link>
            </div>
          </div>
          <div className="card-body">
            <div className="row mb-3">
              <div className="col-md-3">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search products..."
                  name="search"
                  value={filters.search}
                  onChange={handleFilterChange}
                />
              </div>
            </div>
            <div className="table-responsive">
              {tableLoading ? (
                <div className="text-center py-3">
                  <div
                    className="spinner-border spinner-border-sm text-primary"
                    role="status"
                  >
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <span className="ms-2 text-muted">Loading...</span>
                </div>
              ) : (
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>Sr.No</th>
                      <th>Name</th>
                      <th>Grade</th>
                      <th>Type</th>
                      {/* <th>Photo</th> */}
                      <th>Published</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="text-center py-4">
                          <div className="text-muted">
                            <i className="fas fa-search fa-2x mb-2"></i>
                            <p>
                              {filters.search
                                ? `No products found for "${filters.search}"`
                                : "No products found"}
                            </p>
                            {filters.search && (
                              <button
                                className="btn btn-outline-primary btn-sm"
                                onClick={handleClearSearch}
                              >
                                Clear Search
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ) : (
                      products.map((product, index) => (
                        <tr key={index}>
                          <td>{(pagination.current_page - 1) * pagination.per_page + index + 1}</td>
                          <td>{product.name || "N/A"}</td>
                          <td>{product.grade || "N/A"}</td>
                          <td>{product.type || "N/A"}</td>
                          {/* <td>
                            <strong className="small">
                              {
                                product.primary_image && product.primary_image.alt ?
                                  <div className="d-flex align-items-center">
                                    <img 
                                      src={product.primary_image.url} 
                                      alt={product.primary_image.alt || product.name} 
                                      className="me-2 pointer" 
                                      title="View Image"
                                      style={{width: '40px', height: '40px', objectFit: 'cover', borderRadius: '4px'}}
                                    />
                                    <i 
                                      title="Replace Image" 
                                      className="pointer fas fa-sync text-primary fa-lg ms-2" 
                                      onClick={() => handleImageReplace(product)}
                                    ></i>
                                  </div>
                                  :
                                  <span title="Add Photo" className="pointer text-primary" onClick={() => handleImageReplace(product)} >Add Photo</span>
                              }
                            </strong>
                          </td> */}
                          <td>
                            <span
                              className={`badge badge-${product.is_publish ? "success" : "secondary"
                                }`}
                            >
                              {product.is_publish ? "Published" : "Draft"}
                            </span>
                          </td>

                          <td>
                            <div
                              className="d-flex align-items-center"
                              role="group"
                            >
                              <div>
                                <button
                                  className={`btn btn-sm me-2 ${product.is_publish
                                      ? "btn-warning"
                                      : "btn-success"
                                    }`}
                                  onClick={() =>
                                    confirmPublishToggle(
                                      product.product_id,
                                      product.is_publish
                                        ? "unpublish"
                                        : "publish"
                                    )
                                  }
                                >
                                  <i
                                    className={`fas fa-${product.is_publish
                                        ? "times-circle"
                                        : "check-circle"
                                      } me-1`}
                                  />
                                  {product.is_publish ? "Unpublish" : "Publish"}
                                </button>
                              </div>
                              <div>
                                <Link
                                  to={`/products/sanitaryware/${product.product_id}/images`}
                                  title="Images"
                                >
                                  <i className="pointer fas fa-images me-2 fa-lg pt-1"></i>
                                </Link>
                              </div>
                              <div>
                                <Link
                                  to={`/products/sanitaryware/${product.product_id}/edit`}
                                  title="Edit"
                                >
                                  <i className="pointer fas fa-edit text-primary me-2 fa-lg"></i>
                                </Link>
                              </div>

                              <div>
                                <i
                                  className="pointer fas fa-trash text-danger fa-lg"
                                  onClick={() =>
                                    handleDelete(product.product_id)
                                  }
                                ></i>
                              </div>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              )}
            </div>
            {pagination.total_pages > 1 && (
              <div className="mt-3">
                {renderPagination()}
                <div className="text-center mt-2">
                  <small className="text-muted">
                    Showing{" "}
                    {(pagination.current_page - 1) * pagination.per_page + 1} to{" "}
                    {Math.min(
                      pagination.current_page * pagination.per_page,
                      pagination.total_records
                    )}{" "}
                    of {pagination.total_records} entries
                  </small>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {showModal && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable" 
               style={{ 
                 maxWidth: isMobile ? '95vw' : '800px',
                 margin: isMobile ? '10px auto' : 'auto'
               }}>
            <div className="modal-content shadow-xl border-0" 
                 style={{ 
                   borderRadius: isMobile ? '12px' : '8px',
                   maxHeight: isMobile ? '95vh' : '90vh'
                 }}>
              <div className="modal-header" style={{ padding: isMobile ? '15px' : '20px' }}>
                <h5 className="modal-title" style={{ 
                  fontSize: isMobile ? '16px' : '18px',
                  fontWeight: '600'
                }}>
                  Upload Files - {selectedProduct?.name}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseModal}
                  style={{ 
                    fontSize: isMobile ? '14px' : '16px',
                    padding: isMobile ? '8px' : '10px'
                  }}
                ></button>
              </div>
              <div className="modal-body" style={{ 
                padding: isMobile ? '15px' : '20px',
                maxHeight: isMobile ? 'calc(95vh - 120px)' : 'calc(90vh - 120px)',
                overflowY: 'auto'
              }}>
                {/* Tab Navigation - PDF functionality commented out */}
                <ul className="nav nav-tabs mb-3" role="tablist" style={{
                  fontSize: isMobile ? '14px' : '16px'
                }}>
                  {/* PDF tab commented out
                  <li className="nav-item" role="presentation" style={{ flex: 1 }}>
                    <button
                      className={`nav-link ${activeTab === "pdf" ? "active" : ""}`}
                      onClick={() => setActiveTab("pdf")}
                      type="button"
                      style={{
                        padding: isMobile ? '8px 12px' : '10px 16px',
                        fontSize: isMobile ? '13px' : '14px',
                        width: '100%',
                        textAlign: 'center'
                      }}
                    >
                      <i className={`fas fa-file-pdf ${isMobile ? 'me-1' : 'me-2'}`}></i>
                      {isMobile ? 'PDF' : 'PDF Upload'}
                    </button>
                  </li>
                  */}
                  <li className="nav-item" role="presentation" style={{ flex: 1 }}>
                    <button
                      className={`nav-link ${activeTab === "image" ? "active" : ""}`}
                      onClick={() => setActiveTab("image")}
                      type="button"
                      style={{
                        padding: isMobile ? '8px 12px' : '10px 16px',
                        fontSize: isMobile ? '13px' : '14px',
                        width: '100%',
                        textAlign: 'center'
                      }}
                    >
                      <i className={`fas fa-images ${isMobile ? 'me-1' : 'me-2'}`}></i>
                      {isMobile ? 'Images' : 'Image Upload'}
                    </button>
                  </li>
                </ul>

                {/* Tab Content */}
                <div className="tab-content">
                  {/* PDF Tab - commented out */}
                  {/* {activeTab === "pdf" && (
                    <div className="tab-pane fade show active">
                      <div className="row">
                        <div className="col-12">
                          <div className="form-group mb-3">
                            <label className="form-label" style={{
                              fontSize: isMobile ? '14px' : '16px',
                              fontWeight: '500'
                            }}>
                              {selectedProduct?.pdfId ? 'New PDF File (Replace)' : 'PDF File'}
                            </label>
                            <div className="input-group">
                              <input
                                ref={fileInputRef}
                                type="file"
                                className="form-control"
                                id="pdf-files"
                                accept=".pdf"
                                onChange={handlePDFFileChange}
                                required
                                style={{
                                  fontSize: isMobile ? '14px' : '16px',
                                  padding: isMobile ? '8px 12px' : '10px 16px'
                                }}
                              />
                            </div>
                            <small className="form-text text-muted" style={{
                              fontSize: isMobile ? '12px' : '14px'
                            }}>
                              Supported formats: PDF (Max size:{" "}
                              {MAX_PDF_FILE_SIZE}MB per file)
                            </small>
                          </div>
                        </div>
                        <div className="col-12">
                          <div className={`d-flex align-items-center ${isMobile ? 'align-items-start justify-content-between' : 'justify-content-between'}`}>
                            <label htmlFor="fileName" className="mb-1" style={{
                              fontSize: isMobile ? '14px' : '16px',
                              fontWeight: '500'
                            }}>
                              File Name
                            </label>
                            <label className={`${isMobile ? 'mt-2' : 'ms-2 float-end'}`} style={{
                              fontSize: isMobile ? '14px' : '14px'
                            }}>
                              <input 
                                type="checkbox" 
                                checked={hideFirm} 
                                onChange={() => setHideFirm(!hideFirm)} 
                                id="hideFirm" 
                                style={{
                                  marginRight: '5px'
                                }}
                              /> 
                              Hide Firm Name
                            </label>
                          </div>
                          <div className="input-group mt-2">
                            <span className="input-group-text bg-light text-muted small" style={{
                              fontSize: isMobile ? '12px' : '14px',
                              padding: isMobile ? '6px 8px' : '8px 12px',
                              wordBreak: 'break-all'
                            }}>
                              {defaultFileName}
                            </span>
                            <input
                              type="text"
                              className="form-control"
                              name="fileName"
                              value={fileName}
                              id="fileName"
                              placeholder="File Name"
                              onChange={(e) => setFileName(e.target.value)}
                              required
                              style={{
                                fontSize: isMobile ? '14px' : '16px',
                                padding: isMobile ? '8px 12px' : '10px 16px'
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )} */}

                  {/* Image Tab */}
                  {activeTab === "image" && (
                    <div className="tab-pane fade show active">
                      {/* Upload New Image Section */}
                      <div className="mb-4">
                        <div className={`d-flex align-items-center mb-3 ${isMobile ? 'flex-column align-items-start' : ''}`}>
                          <h5 className="mb-0 fw-bold text-dark" style={{
                            fontSize: isMobile ? '16px' : '18px'
                          }}>Upload New Image</h5>
                          <span className={`badge rounded-pill ${isMobile ? 'mt-2' : 'ms-2'}`} style={{ 
                            backgroundColor: '#e3f2fd', 
                            color: '#1976d2',
                            fontSize: isMobile ? '12px' : '14px'
                          }}>
                            {productImages.length}/{MAX_PRODUCT_IMAGE_COUNT} Images
                          </span>
                        </div>

                        <div className="row">
                          {/* Left Column - Image Label */}
                          <div className={`${isMobile ? 'col-12' : 'col-md-6'}`}>
                            <div className="form-group mb-3">
                              <label className="form-label fw-bold" style={{ 
                                color: '#2c5aa0',
                                fontSize: isMobile ? '14px' : '16px'
                              }}>
                                Image Label
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                id="imageLabel"
                                value={imageLabel}
                                onChange={(e) => setImageLabel(e.target.value)}
                                placeholder="e.g. Front View"
                                style={{ 
                                  borderColor: '#e0e0e0',
                                  fontSize: isMobile ? '14px' : '16px',
                                  padding: isMobile ? '8px 12px' : '10px 16px'
                                }}
                              />
                              <small className="form-text text-muted mt-1" style={{
                                fontSize: isMobile ? '12px' : '14px'
                              }}>
                                Optional label for the images
                              </small>
                            </div>
                          </div>

                          {/* Upload & Crop Image */}
                          <div className={`${isMobile ? 'col-12' : 'col-12 col-md-6'}`}>
                            <div className="form-group mb-3">
                              <div className="d-flex align-items-center justify-content-between mb-2">
                                <label className="form-label fw-bold mb-0" style={{ 
                                  color: '#2c5aa0',
                                  fontSize: isMobile ? '14px' : '16px'
                                }}>
                                  Upload & Crop Image
                                </label>
                              </div>
                              {/* Crop Component */}
                              <Crop
                                cropShape="rect"
                                onChangeCropper={(cropData) => {
                                  const { fileCropped, originalFile } = cropData;
                                  const croppedFile = new File(
                                    [fileCropped],
                                    originalFile.name,
                                    {
                                      type: originalFile.type,
                                      lastModified: Date.now(),
                                    }
                                  );
                                  setCroppedFile(croppedFile);
                                }}
                                maxSizeMib={MAX_PRODUCT_IMAGE_SIZE}
                                height={isMobile ? 150 : 200}
                                width={isMobile ? 150 : 200}
                                aspectRatio={1}
                                imagePreview={
                                  croppedFile
                                    ? URL.createObjectURL(croppedFile)
                                    : null
                                }
                                isButtonHidden={false}
                                guid="modal-crop"
                                buttonBackGroundColor="#f8f9fa"
                              />
                            </div>
                          </div>

                          {/* Primary Image Checkbox */}
                          <div className="col-12">
                            <div className="form-check mb-3">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id="isPrimaryCheckbox"
                                checked={isPrimaryImage}
                                onChange={(e) => setIsPrimaryImage(e.target.checked)}
                                style={{
                                  transform: isMobile ? 'scale(1.1)' : 'scale(1)'
                                }}
                              />
                              <label
                                className="form-check-label text-dark fw-normal"
                                htmlFor="isPrimaryCheckbox"
                                style={{
                                  fontSize: isMobile ? '14px' : '16px',
                                  marginLeft: isMobile ? '8px' : '5px'
                                }}
                              >
                                Mark first image as primary
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Product Images Section */}
                      <div>
                        <h5 className="mb-3 fw-bold" style={{ 
                          color: '#006666',
                          fontSize: isMobile ? '16px' : '18px'
                        }}>
                          Product Images ({productImages.length})
                        </h5>

                        {productImages.length === 0 ? (
                          <div className="text-center py-5">
                            <div
                              className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                              style={{
                                width: isMobile ? '60px' : '80px',
                                height: isMobile ? '60px' : '80px',
                                backgroundColor: '#f5f5f5'
                              }}
                            >
                              <i className={`fas fa-images ${isMobile ? 'fa-lg' : 'fa-2x'} text-muted`}></i>
                            </div>
                            <p className="text-muted mb-0" style={{
                              fontSize: isMobile ? '14px' : '16px'
                            }}>No images uploaded yet</p>
                          </div>
                        ) : (
                          <div className="row">
                            {productImages.map((image, index) => (
                              <div
                                key={image.id || index}
                                className={`${isMobile ? 'col-6' : 'col-4 col-md-4'} mb-3`}
                              >
                                <div className="card border-0 shadow-sm h-100" style={{
                                  borderRadius: isMobile ? '12px' : '8px',
                                  overflow: 'hidden'
                                }}>
                                  <div className="card-body p-0">
                                    {/* Image Container */}
                                    <div className="position-relative" style={{
                                      width: "100%",
                                      aspectRatio: "1/1",
                                      overflow: "hidden"
                                    }}>
                                      <img
                                        src={image.image_url}
                                        alt={image.alt || `Product ${index + 1}`}
                                        className="img-fluid"
                                        style={{
                                          width: "100%",
                                          height: "100%",
                                          objectFit: "cover",
                                        }}
                                      />
                                      {image.is_primary && (
                                        <div className="position-absolute top-0 end-0" style={{
                                          margin: isMobile ? '6px' : '8px'
                                        }}>
                                          <span className="badge bg-success" style={{
                                            fontSize: isMobile ? '9px' : '11px',
                                            padding: isMobile ? '3px 5px' : '4px 6px',
                                            borderRadius: isMobile ? '4px' : '6px'
                                          }}>
                                            <i className={`fas fa-star ${isMobile ? '' : 'me-1'}`}></i>
                                            {isMobile ? '' : 'Primary'}
                                          </span>
                                        </div>
                                      )}
                                    </div>

                                    {/* Card Content */}
                                    <div style={{
                                      padding: isMobile ? '8px' : '12px'
                                    }}>
                                      {/* Image Label */}
                                      <div className="text-center mb-2">
                                        <small className="text-dark fw-medium" style={{
                                          fontSize: isMobile ? '11px' : '13px',
                                          lineHeight: '1.2'
                                        }}>{image.label}</small>
                                      </div>

                                      {/* Action Buttons */}
                                      <div className={`d-flex ${isMobile ? 'flex-row' : 'flex-column'} gap-1`} role="group">
                                        {!image.is_primary && (
                                          <button
                                            className="btn btn-outline-primary btn-sm flex-fill"
                                            onClick={() => handleSetPrimary(image.id)}
                                            title="Set as Primary"
                                            style={{
                                              fontSize: isMobile ? '10px' : '12px',
                                              padding: isMobile ? '4px 6px' : '6px 8px',
                                              borderRadius: isMobile ? '6px' : '4px',
                                              minHeight: isMobile ? '28px' : '32px'
                                            }}
                                          >
                                            <i className={`fas fa-star ${isMobile ? '' : 'me-1'}`}></i>
                                            <span className={isMobile ? 'd-none' : 'd-none d-sm-inline'}>Primary</span>
                                          </button>
                                        )}
                                        <button
                                          className="btn btn-outline-danger btn-sm flex-fill"
                                          onClick={() => handleDeleteImage(image.id)}
                                          title="Delete"
                                          style={{
                                            fontSize: isMobile ? '10px' : '12px',
                                            padding: isMobile ? '4px 6px' : '6px 8px',
                                            borderRadius: isMobile ? '6px' : '4px',
                                            minHeight: isMobile ? '28px' : '32px'
                                          }}
                                        >
                                          <i className={`fas fa-trash ${isMobile ? '' : 'me-1'}`}></i>
                                          <span className={isMobile ? 'd-none' : 'd-none d-sm-inline'}>Delete</span>
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer d-flex flex-column flex-sm-row gap-2" style={{
                padding: isMobile ? '15px' : '20px',
                borderTop: '1px solid #e9ecef'
              }}>
                <button
                  type="button"
                  className="btn btn-secondary order-2 order-sm-1"
                  onClick={handleCloseModal}
                  style={{
                    fontSize: isMobile ? '14px' : '16px',
                    padding: isMobile ? '8px 16px' : '10px 20px',
                    width: isMobile ? '100%' : 'auto'
                  }}
                >
                  Close
                </button>
                {/* PDF functionality commented out */}
                {/* {activeTab === "pdf" && (
                  <button
                    type="button"
                    onClick={savePDFUpload}
                    className="btn btn-primary order-1 order-sm-2"
                    disabled={uploading || !fileName || !selectedFile}
                    style={{
                      fontSize: isMobile ? '14px' : '16px',
                      padding: isMobile ? '8px 16px' : '10px 20px',
                      width: isMobile ? '100%' : 'auto'
                    }}
                  >
                    {uploading ? (
                      <>
                        <span
                          className={`spinner-border spinner-border-sm ${isMobile ? 'me-1' : 'me-2'}`}
                          role="status"
                          aria-hidden="true"
                        ></span>
                        {selectedProduct?.pdfId ? 'Replacing...' : 'Uploading...'}
                      </>
                    ) : selectedProduct?.pdfId ?
                      "Replace PDF" :
                      "Upload and Publish"
                    }
                  </button>
                )} */}
                {activeTab === "image" && (
                  <button
                    type="button"
                    onClick={handleImageUpload}
                    className="btn btn-primary order-1 order-sm-2"
                    disabled={
                      uploading ||
                      !croppedFile ||
                      productImages.length >= MAX_PRODUCT_IMAGE_COUNT
                    }
                    style={{ 
                      backgroundColor: '#006666',
                      fontSize: isMobile ? '14px' : '16px',
                      padding: isMobile ? '8px 16px' : '10px 20px',
                      width: isMobile ? '100%' : 'auto'
                    }}
                  >
                    {uploading ? (
                      <>
                        <span
                          className={`spinner-border spinner-border-sm ${isMobile ? 'me-1' : 'me-2'}`}
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Uploading...
                      </>
                    ) : productImages.length >= MAX_PRODUCT_IMAGE_COUNT ? (
                      "Maximum Images Reached"
                    ) : (
                      "Upload Image"
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {showModal && <div className="modal-backdrop fade show"></div>}
      </div>
    </>
  );
};

export default SanitarywareList;
